// config/tanstack.ts
"use client";

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useMemo, useEffect } from "react";
import { apiClient, get, post, URL } from "api";
import { UploadResponse, UploadthingListResponse, StatusResponse } from "@US";
import { useLocalAvatar, useAvatarStore } from "store";

/**
 * Hook para buscar a chave do arquivo de avatar de um usuário.
 * Garante que só exista um arquivo por username, deletando os antigos.
 *
 * @param username O username do qual buscar a chave do arquivo. É obrigatório para este hook.
 * @returns Um objeto UseQueryResult com o estado da query e a chave do arquivo.
 */
export function useUploadthingFile(
  username: string
): UseQueryResult<UploadResponse, Error> {
  const finalUsername = username.toLowerCase().trim();

  const queryKey = useMemo(
    () => ["uploadthing-file", finalUsername],
    [finalUsername]
  );

  return useQuery<UploadResponse, Error>({
    queryKey,
    queryFn: async () => {
      const url = `${URL.out.API_AVATAR_URL}listFiles`;
      const response = await post<UploadthingListResponse>(
        apiClient.avatar,
        url,
        {}
      );
      const files = response.data.files;
      const userFiles = files
        .filter((f) => f.name === finalUsername)
        .sort((a, b) => b.uploadedAt - a.uploadedAt);

      // Deleta arquivos antigos, se houver mais de um
      if (userFiles.length > 1) {
        const oldFileKeys = userFiles.slice(1).map((f) => f.key);
        const deleteUrl = `${URL.out.API_AVATAR_URL}deleteFiles`;
        await post<unknown>(apiClient.avatar, deleteUrl, {
          fileKeys: oldFileKeys,
        });
      }

      return { key: userFiles[0]?.key ?? null };
    },
    enabled: !!finalUsername,
    staleTime: 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook para buscar o status e a URL pública de um arquivo Uploadthing com base na sua chave.
 *
 * @param key A chave do arquivo obtida da query anterior.
 * @returns Um objeto UseQueryResult com o estado da query e os dados do arquivo.
 */
export function useUploadthingStatus(
  key?: string | null
): UseQueryResult<StatusResponse, Error> {
  const queryKey = useMemo(() => ["uploadthing-status", key], [key]);

  return useQuery<StatusResponse, Error>({
    queryKey,
    queryFn: async () => {
      const url = `${URL.out.API_AVATAR_URL}pollUpload/${key}`;
      const response = await get<StatusResponse>(apiClient.avatar, url);
      return response.data;
    },
    enabled: !!key,
    staleTime: 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

/**
 * Atualiza a URL do avatar no store global de avatar, se necessário.
 */
export function setAvatarCache(username: string, avatarUrl: string) {
  const current = useAvatarStore.getState().avatars[username];
  if (current !== avatarUrl) {
    useAvatarStore.getState().setAvatar(username, avatarUrl);
  }
}

/**
 * @summary
 * Hook que encapsula a lógica de queries dependentes para obter a URL de um avatar de usuário.
 * Primeiro busca a 'key' do arquivo e, em seguida, usa essa 'key' para obter a URL.
 *
 * @param username O username **obrigatório** para o qual buscar a URL do avatar.
 * @returns Um objeto com o estado de carregamento, erro e a URL do avatar.
 */
export function useUserAvatarUrl(username: string) {
  const fileQuery = useUploadthingFile(username);
  const key = fileQuery.data?.key;
  const statusQuery = useUploadthingStatus(key);

  const isLoading = fileQuery.isLoading || statusQuery.isLoading;
  const isError = fileQuery.isError || statusQuery.isError;
  const error = fileQuery.error || statusQuery.error;

  const apiFileUrl = useMemo(
    () => statusQuery.data?.fileData?.fileUrl,
    [statusQuery.data?.fileData?.fileUrl]
  );

  // Busca local first do avatar
  const localFileUrl = useLocalAvatar(username);

  // Sempre que obtiver uma nova URL da API, salva no store global se for diferente
  useEffect(() => {
    if (apiFileUrl && username && localFileUrl !== apiFileUrl) {
      setAvatarCache(username, apiFileUrl);
    }
  }, [apiFileUrl, username, localFileUrl]);

  return {
    isLoading,
    isError,
    error,
    fileUrl: localFileUrl || apiFileUrl,
    refetchFileKey: fileQuery.refetch,
    refetchStatus: statusQuery.refetch,
  };
}
